import { useTranslation } from 'react-i18next';
import { TableCell } from '@anytag/shared-atoms';
import { ColumnDef } from '@tanstack/react-table';
import { formatIntNumber } from '@anytag/shared-utils';
import { usePostDetailsParam } from '@anytag/shared-hooks';
import { InfluencerSocialPost, PostListSortField, SocialPostType } from '@anytag/graphql';

interface Props {
  socialPostType: SocialPostType;
}

export const useInfluencerScrapingPostsListTable = ({ socialPostType }: Props) => {
  const { t } = useTranslation();
  const { setPostDetailsParam } = usePostDetailsParam();

  const isYoutube = socialPostType === SocialPostType.YOUTUBE;
  const isTwitter = socialPostType === SocialPostType.TWITTER;
  const isThreads = socialPostType === SocialPostType.THREADS;
  const isInstagram = socialPostType === SocialPostType.INSTAGRAM;
  const isFacebook = [SocialPostType.FACEBOOK, SocialPostType.FACEBOOK_PAGE].includes(socialPostType);
  const isInstagramTypes = [
    SocialPostType.INSTAGRAM,
    SocialPostType.INSTAGRAM_REEL,
    SocialPostType.INSTAGRAM_STORY
  ].includes(socialPostType);

  const columns: ColumnDef<InfluencerSocialPost>[] = [
    {
      accessorKey: 'post',
      header: t<string>('HeaderColumn.Post'),
      cell: ({ row }) => {
        const { id, postUrl, caption, thumbnail } = row.original;

        return (
          <TableCell.Post
            postUrl={postUrl}
            title={caption || ''}
            thumbnail={thumbnail || ''}
            socialPostType={socialPostType}
            onClick={() => setPostDetailsParam(id)}
          />
        );
      }
    },
    {
      accessorKey: 'impressions',
      header: t<string>('HeaderColumn.Impressions'),
      meta: {
        align: 'right',
        sortField: PostListSortField.IMPRESSION,
        hidden: !(isTwitter || isFacebook || isInstagram),
        help: t<string>('Tooltip.impressionsHelpForScrapingAccounts')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.impressions)} />
    },
    {
      accessorKey: 'views',
      header: t<string>('HeaderColumn.View'),
      meta: {
        align: 'right',
        sortField: PostListSortField.VIEW,
        help: t<string>('Tooltip.ViewHelp'),
        hidden: isTwitter || isFacebook || isThreads || isInstagram
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.views)} />
    },
    {
      accessorKey: 'reach',
      header: t<string>('HeaderColumn.Reach'),
      meta: {
        align: 'right',
        hidden: !isInstagramTypes,
        sortField: PostListSortField.REACH,
        help: t<string>('Tooltip.ReachPostHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.reach)} />
    },
    {
      accessorKey: 'likes',
      header: t<string>(isFacebook ? 'HeaderColumn.Reaction' : 'HeaderColumn.Like'),
      meta: {
        align: 'right',
        sortField: PostListSortField.LIKE,
        help: t<string>('Tooltip.LikeHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.likes)} />
    },
    {
      accessorKey: 'comments',
      header: t<string>(isTwitter || isThreads ? 'HeaderColumn.Replies' : 'HeaderColumn.Comment'),
      meta: {
        align: 'right',
        sortField: PostListSortField.COMMENT,
        help: t<string>('Tooltip.CommentHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.comments)} />
    },
    {
      accessorKey: 'shares',
      header: t<string>(isTwitter ? 'HeaderColumn.Retweets' : 'HeaderColumn.share'),
      meta: {
        align: 'right',
        hidden: isYoutube || isThreads,
        sortField: PostListSortField.SHARE,
        help: t<string>('Tooltip.shareHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.shares)} />
    },
    {
      accessorKey: 'saved',
      header: t<string>('HeaderColumn.Save'),
      meta: {
        align: 'right',
        hidden: !isInstagramTypes,
        sortField: PostListSortField.SAVED,
        help: t<string>('Tooltip.SaveHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.saved)} />
    },
    {
      accessorKey: 'date',
      header: t<string>('HeaderColumn.Post date'),
      meta: { sortField: PostListSortField.POST_DATE, help: t<string>('Tooltip.PostDateHelp') },
      cell: ({ row }) => <TableCell.Date className="min-w-20" value={row.original.date} />
    }
  ];

  return { columns };
};
